import { useCallback, useEffect, useState } from 'react'
import { FlaskConical, RefreshCw } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { API_BASE } from '@/lib/api'
import { fetchEnv, DT_STR as S, type EnvInfo } from './devtoolsApi'

/** A green/red status pill for a live boolean (DB up / Redis up / gate on). */
function StatusPill({ on, onLabel, offLabel }: { on: boolean; onLabel: string; offLabel: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
        on ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-green-500' : 'bg-red-500'}`} />
      {on ? onLabel : offLabel}
    </span>
  )
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border-default px-4 py-2.5 text-sm last:border-b-0">
      <span className="text-secondary">{label}</span>
      <span className="font-mono text-primary">{children}</span>
    </div>
  )
}

/**
 * A27.07 — Песочница / environment panel. FUNCTIONAL: reads the real
 * `/admin/devtools/env` (deployment mode, build/Go version, live Postgres +
 * Redis reachability, restart gate, feature flags) and folds in client-known
 * env. Read-only; the endpoint enforces access.
 */
export function SandboxScreen() {
  const [info, setInfo] = useState<EnvInfo | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const probe = useCallback(async (signal?: AbortSignal) => {
    setBusy(true)
    const res = await fetchEnv(signal)
    if (signal?.aborted) return
    setInfo(res.data)
    setErr(res.ok ? null : (res.error ?? S.failed))
    setBusy(false)
  }, [])

  useEffect(() => {
    const ac = new AbortController()
    probe(ac.signal)
    return () => ac.abort()
  }, [probe])

  const host = window.location.hostname
  const isDev = host === 'localhost' || host.startsWith('127.') || host.endsWith('.local')

  return (
    <>
      <PageHeader
        title={S.sandbox}
        screenId="A27.07"
        actions={
          <button
            type="button"
            onClick={() => probe()}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary disabled:opacity-50"
          >
            <RefreshCw size={13} className={busy ? 'animate-spin' : undefined} />
            {S.refresh}
          </button>
        }
      />
      <div className="space-y-4 p-4">
        {/* Client-known */}
        <div className="max-w-2xl rounded-md border border-border-default bg-surface-1">
          <InfoRow label={S.apiBase}>{API_BASE}</InfoRow>
          <InfoRow label={S.clientEnv}>{isDev ? 'development' : 'production'}</InfoRow>
        </div>

        {/* Backend-reported environment */}
        <div className="mb-1 flex items-center gap-2 text-sm font-medium text-secondary">
          <FlaskConical size={16} className={info ? 'text-green-500' : 'text-secondary'} />
          <span>
            {S.sandboxInfo} · {busy ? S.running : info ? S.ok : err ? S.failed : ''}
          </span>
        </div>

        {info && (
          <div className="max-w-2xl rounded-md border border-border-default bg-surface-1">
            <InfoRow label={S.backendEnv}>{info.env}</InfoRow>
            <InfoRow label={S.buildVersion}>{info.version}</InfoRow>
            <InfoRow label={S.goVersion}>{info.go_version}</InfoRow>
            <InfoRow label={S.postgres}>
              <StatusPill on={info.postgres} onLabel={S.online} offLabel={S.offline} />
            </InfoRow>
            <InfoRow label={S.redis}>
              <StatusPill on={info.redis} onLabel={S.online} offLabel={S.offline} />
            </InfoRow>
            <InfoRow label={S.restartGate}>
              <StatusPill on={info.restart_enabled} onLabel={S.yes} offLabel={S.no} />
            </InfoRow>
          </div>
        )}

        {info && (
          <div className="max-w-2xl">
            <div className="mb-1 text-sm font-medium text-secondary">{S.featureFlags}</div>
            <div className="rounded-md border border-border-default bg-surface-1">
              {info.feature_flags.length === 0 ? (
                <div className="px-4 py-3 text-sm text-secondary">{S.noFlags}</div>
              ) : (
                info.feature_flags.map((f) => (
                  <div
                    key={f.name}
                    className="flex items-center justify-between border-b border-border-default px-4 py-2 text-sm last:border-b-0"
                  >
                    <span className="font-mono text-primary">{f.name}</span>
                    <span className="font-mono text-secondary">{f.value}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {!info && err && (
          <pre className="max-w-2xl overflow-auto rounded-md border border-border-default bg-surface-1 p-3 font-mono text-xs text-red-600">
            {err}
          </pre>
        )}
      </div>
    </>
  )
}
